import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtDate, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import './Journal.css';

const ACCOUNT_TYPES = ['Ledger', 'Cash', 'Bank', 'Customer', 'Supplier', 'Sales', 'Expense', 'Other'];

const EMPTY = {
  entry_date: today(),
  debit: { account_type: 'Ledger', ledger_id: '', bank_id: '', customer_id: '', supplier_id: '', party_name: '' },
  credit: { account_type: 'Ledger', ledger_id: '', bank_id: '', customer_id: '', supplier_id: '', party_name: '' },
  amount: '',
  narration: '',
};

export default function Journal() {
  const { t } = useTranslation();
  const { canAccess } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg] = useState('');
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 4000); };

  const { data: entries, loading, error, reload } = useApi(() => api.journal.list(), []);
  const { data: banks } = useApi(() => api.banks.list(), [], { banks: [] });
  const { data: customers } = useApi(() => api.customers.list(), [], []);
  const { data: suppliers } = useApi(() => api.suppliers.list(), [], []);
  const { data: ledgers } = useApi(() => api.ledgers.list(), [], { rows: [] });

  if (!canAccess('accounting')) {
    return (
      <div className="jv-page">
        <div className="page-header"><h2 className="page-title">{t('journal.title')}</h2></div>
        <ErrBanner msg={t('journal.accessDenied')} />
      </div>
    );
  }

  const updateLeg = (side, field, value) => {
    setForm(f => ({ ...f, [side]: { ...f[side], [field]: value } }));
  };

  const handleSave = async () => {
    if (!form.debit.account_type) { notify('❌ ' + t('journal.errors.selectDebit')); return; }
    if (!form.credit.account_type) { notify('❌ ' + t('journal.errors.selectCredit')); return; }
    const amt = parseFloat(form.amount || 0);
    if (amt <= 0) { notify('❌ ' + t('journal.errors.amountPositive')); return; }
    if (!form.narration.trim()) { notify('❌ ' + t('journal.errors.narrationRequired')); return; }

    // Build clean leg payloads — only include fields that matter
    const cleanLeg = (leg) => {
      const out = { account_type: leg.account_type, party_name: leg.party_name || '' };
      if (leg.account_type === 'Ledger' && leg.ledger_id) out.ledger_id = parseInt(leg.ledger_id);
      if (leg.account_type === 'Bank' && leg.bank_id) out.bank_id = parseInt(leg.bank_id);
      if (leg.account_type === 'Customer' && leg.customer_id) out.customer_id = parseInt(leg.customer_id);
      if (leg.account_type === 'Supplier' && leg.supplier_id) out.supplier_id = parseInt(leg.supplier_id);
      return out;
    };

    setSaving(true);
    try {
      await api.journal.create({
        entry_date: form.entry_date,
        debit: cleanLeg(form.debit),
        credit: cleanLeg(form.credit),
        amount: amt,
        narration: form.narration.trim(),
      });
      notify('✅ ' + t('journal.toasts.posted'));
      setForm(EMPTY);
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const isIdentical = () => {
    const d = form.debit, c = form.credit;
    if (!d.account_type || !c.account_type) return false;
    if (d.account_type !== c.account_type) return false;
    if (d.account_type === 'Ledger' && d.ledger_id === c.ledger_id) return !!d.ledger_id;
    if (d.account_type === 'Bank' && d.bank_id === c.bank_id) return !!d.bank_id;
    if (d.account_type === 'Customer' && d.customer_id === c.customer_id) return !!d.customer_id;
    if (d.account_type === 'Supplier' && d.supplier_id === c.supplier_id) return !!d.supplier_id;
    if (['Cash', 'Sales', 'Expense', 'Other'].includes(d.account_type)) {
      return (d.party_name || d.account_type) === (c.party_name || c.account_type);
    }
    return false;
  };

  const handleDelete = async () => {
    try {
      await api.journal.remove(confirm.id);
      notify('✅ ' + t('journal.toasts.reversed'));
      reload();
    } catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  return (
    <div className="jv-page">
      <div className="page-header">
        <h2 className="page-title">{t('journal.title')}</h2>
        <span style={{ fontSize: 12, color: 'var(--warning)' }}>
          {t('journal.adminOnly')}
        </span>
      </div>

      {msg && <div className="jv-toast">{msg}</div>}
      {error && <ErrBanner msg={error} />}

      <div className="section-card">
        <div className="section-header">{t('journal.newVoucher')}</div>

        <div className="jv-form-top">
          <div className="form-group">
            <label>{t('journal.transactionDate')}</label>
            <input type="date" value={form.entry_date}
              onChange={e => setForm({ ...form, entry_date: e.target.value })} />
          </div>
        </div>

        <div className="jv-amount-section">
          <div className="form-group jv-amount-group">
            <label>{t('journal.transferAmount')}</label>
            <input type="number" step="0.01" value={form.amount}
              onChange={e => setForm({ ...form, amount: e.target.value })}
              placeholder="0.00" className="jv-amount-input" />
          </div>
        </div>

        {/* Debit / Credit legs side-by-side */}
        <div className="jv-legs">
          <LegEditor
            title={t('journal.debit')} side="debit" tone="green"
            leg={form.debit} onChange={(f, v) => updateLeg('debit', f, v)}
            banks={banks.banks || []} customers={customers} suppliers={suppliers} ledgers={ledgers}
          />
          <LegEditor
            title={t('journal.credit')} side="credit" tone="orange"
            leg={form.credit} onChange={(f, v) => updateLeg('credit', f, v)}
            banks={banks.banks || []} customers={customers} suppliers={suppliers} ledgers={ledgers}
          />
        </div>

        <div className="jv-narration">
          <label>{t('journal.narration')}</label>
          <textarea rows={2} value={form.narration}
            onChange={e => setForm({ ...form, narration: e.target.value })}
            placeholder={t('journal.narrationPlaceholder')} />
        </div>

        <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
          {isIdentical() && (
            <span style={{ color: 'var(--danger)', fontSize: 12, fontWeight: 600 }}>
              {t('journal.sameAccountError')}
            </span>
          )}
          <button className="btn btn-primary btn-lg"
            disabled={saving || !form.amount || parseFloat(form.amount) <= 0 || isIdentical()}
            onClick={handleSave}>
            {saving ? t('journal.posting') : t('journal.postEntry', { amount: fmtNum(form.amount || 0) })}
          </button>
        </div>
      </div>

      {loading ? <Spinner /> : (
        <div className="section-card" style={{ overflowX: 'auto' }}>
          <div className="section-header">{t('journal.recentVouchers')}</div>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ textAlign: 'left' }}>{t('common.date')}</th>
                <th style={{ textAlign: 'left' }}>{t('journal.jvNo')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.debit')}</th>
                <th style={{ textAlign: 'left' }}>{t('common.credit')}</th>
                <th>{t('common.amount')}</th>
                <th style={{ textAlign: 'left' }}>{t('journal.narrationCol')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(entries || []).length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>
                  {t('journal.noVouchers')}
                </td></tr>
              ) : (entries || []).map(e => (
                <tr key={e.id}>
                  <td style={{ textAlign: 'left' }}>{fmtDate(e.entry_date)}</td>
                  <td style={{ textAlign: 'left', fontFamily: 'monospace', fontWeight: 600 }}>{e.reference_no}</td>
                  <td style={{ textAlign: 'left' }}>
                    <span className="jv-leg-pill jv-leg-dr">{e.dr_account}</span>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{e.dr_party}</div>
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    <span className="jv-leg-pill jv-leg-cr">{e.cr_account}</span>
                    <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>{e.cr_party}</div>
                  </td>
                  <td style={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    ₹ {fmtNum(e.amount)}
                  </td>
                  <td style={{ textAlign: 'left', fontSize: 12, color: 'var(--text-secondary)' }}>
                    {e.narration || '—'}
                  </td>
                  <td>
                    <button className="btn btn-sm" style={{ color: 'var(--danger)' }}
                      onClick={() => setConfirm(e)}>🗑</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={t('journal.reverseTitle')}
        message={confirm ? t('journal.reverseMessage', { ref: confirm.reference_no }) : ''}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
        confirmLabel={t('journal.reverse')}
      />
    </div>
  );
}


/* ── Single-leg editor (used for both Debit and Credit) ── */
function LegEditor({ title, tone, leg, onChange, banks, customers, suppliers, ledgers }) {
  const { t } = useTranslation();
  const isLedger = leg.account_type === 'Ledger';
  const isBank = leg.account_type === 'Bank';
  const isCustomer = leg.account_type === 'Customer';
  const isSupplier = leg.account_type === 'Supplier';
  const isOther = ['Cash', 'Sales', 'Expense', 'Other'].includes(leg.account_type);

  return (
    <div className={`jv-leg jv-leg-${tone}`}>
      <div className="jv-leg-title">{title}</div>

      <div className="form-group">
        <label>{t('journal.accountType')}</label>
        <select value={leg.account_type} onChange={e => onChange('account_type', e.target.value)}>
          <option value="">{t('journal.selectDash')}</option>
          {ACCOUNT_TYPES.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      {isLedger && (
        <div className="form-group">
          <label>{t('journal.selectLedger')}</label>
          <select value={leg.ledger_id} onChange={e => onChange('ledger_id', e.target.value)}>
            <option value="">{t('journal.selectAccount')}</option>
            {(ledgers.rows || []).map(l => (
              <option key={l.ledger_id} value={l.ledger_id}>
                {l.name} ({l.code})
              </option>
            ))}
          </select>
        </div>
      )}

      {isBank && (
        <div className="form-group">
          <label>{t('common.bank')}</label>
          <select value={leg.bank_id} onChange={e => onChange('bank_id', e.target.value)}>
            <option value="">{t('journal.selectDash')}</option>
            {banks.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      )}

      {isCustomer && (
        <div className="form-group">
          <label>{t('common.customer')}</label>
          <select value={leg.customer_id} onChange={e => onChange('customer_id', e.target.value)}>
            <option value="">{t('journal.selectDash')}</option>
            {(customers || []).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      )}

      {isSupplier && (
        <div className="form-group">
          <label>{t('common.supplier')}</label>
          <select value={leg.supplier_id} onChange={e => onChange('supplier_id', e.target.value)}>
            <option value="">{t('journal.selectDash')}</option>
            {(suppliers || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      {isOther && (
        <div className="form-group">
          <label>{t('journal.partyLabel')}</label>
          <input value={leg.party_name}
            onChange={e => onChange('party_name', e.target.value)}
            placeholder={leg.account_type || t('common.description')} />
        </div>
      )}
    </div>
  );
}
